import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { CashPlusPortfolioApi, ICashPlusFiatWallet } from "@modules/cashplus/entities/cashplus.model";
import { computed, Directive, inject, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject, Subscription } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import {
	Conversion,
	Cryptocoin,
	CryptoWallet,
	ErrorClass,
	Fiat,
	FiatWallet,
	LimitError,
	Offer,
	OfflineError,
	Price,
	ValidationError
} from "@models";

import { StepSummaryModal } from "../../main/components/step-summary/step-summary.modal";

import { FiatService } from "@main-services/currency/fiat.service";

import { Helper } from "@helpers/functions";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableTradeSummaryComponent } from "../components/summary/tradable-trade-summary.component";
import { MetalWallet } from "@models/asset/metal/metal_wallet";
import { Currency } from "@models/interfaces/currency";
import { TradeOfferService } from "../services/trade-offer.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { Metal } from "@models/asset/metal/metal";
import {
	APPROPRIATENESS_REQUIRED_ERROR_CODE,
	FIRST_TRADE_EXECUTED,
	TOKEN_BEST_ID,
	TRADABLES_TYPE
} from "@helpers/constants";
import { Derivative } from "@models/derivative/currencies/derivative";
import { SELECTED_CURRENCY, TRADE_TYPE } from "@models/trade-offer.model";
import { OfflineModalComponent } from "../../main/components/offline-modal/offline-modal.component";
import { round, stringToNumberWithPrecision, stripFunnyInput } from "@helpers/utils";
import {
	convertFiatToTradable,
	convertTradableToFiat,
	toCurrencyByFiatId,
	toEuroByFiatId
} from "@helpers/tradable-convert";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { AccountService } from "../../account/services/account.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { Best2TermsModalComponent } from "./best-2-terms-modal/components/best-2-terms-modal.component";
import { ITBStep } from "@modules/shared/components/instant-trade-bonus/entitites/itb-step";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { ITBTradeSuccess } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade-success";
import { TradeSuccessV1Component } from "@modules/main/components/step-summary/success-screens/trade-success/trade-success-redesign/trade-success-redesign.component";
import { TradeSuccessComponent } from "@modules/main/components/step-summary/success-screens/trade-success/trade-success.component";
import { map, take } from "rxjs/operators";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { ILeverageTokenInfoSummaryViewModel } from "@modules/main/components/leveraged-tokens/leverage-token-info-summary/leverage-token-info-summary.interface";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { AgreementBundle } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { LegalAcceptanceComponent } from "@modules/main/components/legal-acceptance-modal/legal-acceptance.component";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { CheckoutRemoteService } from "@modules/checkout/data-access/checkout.remote";
import { EstimatePricesQueryVariables } from "../../../api/graphql/offer/resources/generated/estimate.prices.service.graphql-gen";
import { TradeType } from "@gql/offer";
import { mapEstimateIndexPricesToPrice, mapEstimatePricesToPrice } from "@modules/trade/helpers/helper";
import { EstimateIndexPricesQueryVariables } from "../../../api/graphql/offer/generated/estiamte-index.prices.service.graphql-gen";

@UntilDestroy()
@Directive()
export abstract class TradeComponent extends StepSummaryModal implements OnInit, OnDestroy {
	private apprServiceHelper = inject(StockTestHelperService);
	private termsAndConditionsService = inject(TermsAndConditionsService);
	private statsigService = inject(StatsigClientService);
	private checkoutRemote = inject(CheckoutRemoteService);

	@ViewChild(TradableTradeSummaryComponent) public offer = null;
	protected cashPlusPortfolioFacade = inject(CashplusPortfolioFacade);
	protected cashPlusFacade = inject(CashPlusFacade);

	public old: Offer = new Offer();
	public priceSubject$: BehaviorSubject<Price> = new BehaviorSubject<Price>(null);
	public showTradeFlow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public data: Offer;

	protected category: string = "trade";
	protected amountChanged: boolean = false;
	protected _bfc_value: string = "";
	protected filterTypes: (typeof Currency)[] = [Currency, Tradable, Cryptocoin, Metal, Index];

	private bestDefaultWallet: TradableWallet = null;
	private tradingFlags$: Subscription = null;
	private canTradeDerivativesViaGql: boolean = false;
	private canTradeBciViaGql: boolean = false;

	minFromEstimate = signal<number | null>(null);

	protected pageChangeSubject: Subject<void> = new Subject();
	protected readonly flowName: string = "";
	protected readonly pathPrepend: string = "";
	protected successTradeId: string = "";

	protected preTransBalance: number = 0;
	public leverageTokenViewModel$: Observable<ILeverageTokenInfoSummaryViewModel> =
		this.leveragedTokenFacade.getLeverageTokenStateInfo$();

	// cashplus
	protected selectedCashPlusWallet: ICashPlusFiatWallet = null;
	public cashPlusPortfolioForFiat$: Observable<CashPlusPortfolioApi> = EMPTY;
	public cashPlusEnabled$: Observable<boolean> = this.cashPlusFacade.isCashPlusEnabled$();

	//Limit Order
	@Input() isLimitOrder: boolean = false;
	@Input() cameFromHeaderSwitch: boolean = false;

	public constructor(
		protected apicallService: ApicallService,
		protected fiatwalletService: FiatWalletService,
		protected infoService: InfoService,
		protected tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		protected walletService: TradableWalletListService,
		protected userService: UserService,
		protected tradeOfferService: TradeOfferService,
		protected launchDarklyService: LaunchDarklyService,
		protected confirmModalService: ConfirmModalService,
		protected leveragedTokenFacade: LeveragedTokensFacade
	) {
		super(confirmModalService);
		this.setCurrencyFilterTypes();
	}

	public ngOnInit(): void {
		this.termsAndConditionsService.checkTCaccepted((bundles: AgreementBundle[]) => {
			this.modalService.hideAll();
			this.modalService.showModal(LegalAcceptanceComponent, { bundles });
		});
		// What?
		if (this.data.getWalletId() !== "") {
			// eslint-disable-next-line no-self-assign
			this.wallet = this.wallet;
			this.preTransBalance = this.wallet.getBalance();
		}

		// If the tradable is known
		if (this.data.getCurrencyId() !== "") {
			this.tradableId = this.data.getCurrencyId();
		}

		// If directly from wallet
		if (this.data.getDirectTradableWalletOffer()) {
			if (this.data.getType() === TRADE_TYPE.BUY) {
				this.selectSoleFiatWallet();
			}
		}

		if (this.data.getDirectTradableWalletOffer() && this.data.getFiatId() && this.data.getFiatWalletId()) {
			this.edit = 0;
			this.updateAmounts();
		}

		if (this.data.getFiatId() && this.data.getFiatWalletId() && this.data.isDirectBestOffer()) {
			// Direct route for BEST upselling buttons
			this.data.setUserWants(SELECTED_CURRENCY.CRYPTOCOIN);
			this.updateAmounts();
		}

		this.walletService
			.getList()
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.setBestDefaultWallet();
			});
		this.setBestDefaultWallet();

		super.ngOnInit();

		this.initTracking();

		this.pageChangeSubject.next();

		this.rudderstackFlowState("started");
		this.onGqlFeatureFlagChange();
	}

	public ngOnDestroy(): void {
		this.pageChangeSubject.complete();
		this.leveragedTokenFacade.resetState();

		this.rudderstackFlowState(this.saved ? "successful" : "cancelled");

		if (this.tradingFlags$) {
			this.tradingFlags$.unsubscribe();
		}
	}

	public getInputFormat(): string {
		return this.getSelectedCurrency();
	}

	public closeAll(b: boolean = true): void {
		this.resetOffer();

		super.closeAll(b);
	}

	public showGreyBackground(): boolean {
		return (
			((!!this.data.getCurrencyId() && !this.isFirstStep) || !!this.tradableId) && this.showTradeFlow$.getValue()
		);
	}

	public isWalletStackSelection(): boolean {
		return this.isFirstStep && this.showGreyBackground();
	}

	public isTradableListSelection(): boolean {
		return this.isFirstStep && !this.showGreyBackground();
	}

	public get userWantsTradable(): boolean {
		return this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN;
	}

	public closeFromError(isSummaryError: boolean = false): void {
		if (isSummaryError) {
			this.summary = false;
		}
		this.resetOffer();
		super.close();
	}

	public close(): void {
		this.resetOffer();
		super.close();
	}

	public showDerivativeFooter(): boolean {
		return this.getTradable() instanceof Derivative;
	}

	public setEdit(e: number, summary = false): void {
		this.showTradeFlow$.next(!summary);
		super.setEdit(e, summary);
		if (!this.isEdit(3)) {
			// Clear price if edit goes to asset or fiat selection
			this.priceSubject$.next(null);
		} else if (this.isEdit(3) && !this.priceSubject$.getValue()) {
			// Edgecase for multi-edit scenarios
			this.updateAmounts();
		}
		this.pageChangeSubject.next();
	}

	public save(): void {
		return;
	}

	private getPriceValue(): Price {
		return this.priceSubject$.getValue();
	}

	public get bfcValue(): string {
		return this._bfc_value;
	}

	public set bfcValue(s: string) {
		this._bfc_value = s;
	}

	public done(): void {
		this.fiatwalletService.transactionReceived();
		this.walletService.services.forEach((s) => s.transactionReceived());
		super.done();
	}

	public get icon(): string {
		return this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN
			? this.getTradable().getLogo()
			: this.getFiat().getLogo();
	}

	public precision(): number {
		if (this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN) {
			return this.getTradable().getPrecision();
		}

		return this.getFiat().getPrecision();
	}

	protected setCurrencyFilterTypes(): void {
		this.filterTypes = this.filterTypes.filter((type) =>
			this.filterByAction(
				this.tradableListService.list
					.filter((item) => !item.isIndexOnly()) // Checks filterType array for items that are buy or sell active. If no item of a specific tradable-type is active, that filter-option is not shown
					.filter((item) => item instanceof type)
			)
		);
	}

	protected filterByAction(items: Tradable[]): boolean {
		return items.some((item) => item.isBuyActive()); // Default is buy active, sell active is overwritten
	}

	private setBestDefaultWallet(): void {
		this.bestDefaultWallet = this.walletService
			.getWalletsByTradableId(TOKEN_BEST_ID)
			.find((wallet) => wallet.isDefault());
	}

	public getSelectedCurrency(): SELECTED_CURRENCY {
		return this.data.getSelectedCurrency();
	}

	public change(): void {
		this.data.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN
			? this.data.setUserWants(SELECTED_CURRENCY.FIAT)
			: this.data.setUserWants(SELECTED_CURRENCY.CRYPTOCOIN);
		this.rudderstackTrack("Trade-By Used", {
			trade_by:
				this.data.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN
					? this.getFiat().getSymbol().toLowerCase()
					: this.getTradable().getSymbol().toLowerCase(),
			flow: this.flowName
		});
	}

	public isIndexTrade(): boolean {
		return this.getTradable() instanceof Index;
	}

	public isLeveragedToken(): boolean {
		return this.getTradable() instanceof LeveragedToken;
	}

	public isNotIndexNorTokenTrade(): boolean {
		return !this.isIndexTrade() && !this.getTradable().isToken();
	}

	public get wallet(): TradableWallet {
		return this.walletService.getItemById(this.data.getWalletId());
	}

	public set wallet(w: TradableWallet) {
		this.data.setWalletId(w.getId());
		this.data.setCurrencyId(w.getCurrencyId());
		this.data.setIndexTrade(this.isIndexTrade());
	}

	public get fiatWallet(): FiatWallet {
		return this.fiatwalletService.getItemById(this.data.getFiatWalletId());
	}

	public set fiatWallet(fw: FiatWallet) {
		this.data.setFiatWalletId(fw.getId());
		this.data.setFiatId(fw.getFiatId());
		this.setCashPlusPortfolioForFiat();
	}

	public getTradable(): Tradable {
		return this.tradableListService.getItemById(this.data.getCurrencyId()) || new Cryptocoin();
	}

	public getFiat(): Fiat {
		if (this.data.getFiatId() === "") {
			this.setDefaultFiatId();
		}

		return this.fiatService.getItemById(this.data.getFiatId());
	}

	public setDefaultFiatId(): void {
		this.data.setFiatId(this.accountService.account.getFiatId());
	}

	public getStepsFromOfferFiat(): number {
		return this.fiatService.stepSizeByItem(this.getFiat());
	}

	public getOffer(): void {
		if (!this.sufficientBestAmount && this.data.bestFeeOptIn) {
			this.data.bestFeeOptIn = false;
			this.infoService.setInfo(this.translationService.translate("disabled_best_opt_in"));
		}

		let tradableWalletBalance = this.wallet.getBalance();
		if (this.isIndexTrade()) {
			// As Index balance is always in EUR, it has to be converted into the selected Fiat
			tradableWalletBalance = toCurrencyByFiatId(
				tradableWalletBalance,
				this.fiatService.getItemById(this.data.getFiatId())
			);
		}
		// Selected amount in tradable
		if (this.userWantsTradable) {
			// Sell amount is larger than balance
			if (this.data.getType() === TRADE_TYPE.SELL && +this.amount > tradableWalletBalance) {
				this.amount = this.maxAmount().tradableAmount();
				this.infoService.setError(this.translationService.translate("sell_amount_larger_than_wallet_balance"));
				return;
			}

			// if we have cash plus balance, use the combined fiat balance to trade, otherwise use just normal fiat balance
			const cashPlusBalance = +this.selectedCashPlusWallet?.assetBalance || 0;
			const totalBalance = cashPlusBalance || this.fiatWallet.getBalance();

			// Buy amount is larger than balance
			if (this.data.getType() === TRADE_TYPE.BUY && this.toFiatAmount(+this.amount) > totalBalance) {
				this.amount = this.maxAmount().tradableAmount();
				this.infoService.setError(this.translationService.translate("buy_amount_larger_than_wallet_balance"));
				return;
			}
			// Amount smaller than min
			if (+this.data.getAmountTradable() < this.toTradableAmount(this.min())) {
				this.infoService.setError(this.translationService.translate("transaction_amount_low"));
				this.data.setUserWants(SELECTED_CURRENCY.FIAT);
				this.amount = this.min + "";
			} else {
				this.data.setAmountDefinition(this.getSelectedCurrency());
				this.callTradeOffer();
			}
			// Amount selected in fiat
		} else {
			// sell max
			if (
				this.data.getType() === TRADE_TYPE.SELL &&
				this.isIndexTrade() &&
				this.toTradableAmount(+this.amount) === +this.getPriceValue()?.getMaxAmount().fiatAmount()
			) {
				this.data.setSellAll(true);
			}

			// Sell more than balance
			if (
				this.data.getType() === TRADE_TYPE.SELL &&
				(this.isIndexTrade() ? +this.amount : this.toTradableAmount(+this.amount)) > tradableWalletBalance
			) {
				this.amount = this.maxAmount().fiatAmount();
				if (this.isIndexTrade()) {
					this.data.setSellAll(true);
				}
				this.infoService.setError(this.translationService.translate("sell_amount_larger_than_wallet_balance"));
				return;
			}

			// Wallet balance for BCI
			if (
				this.data.getType() === TRADE_TYPE.BUY &&
				this.isIndexTrade() &&
				+this.amount > this.fiatWallet.getBalance()
			) {
				this.amount = this.maxAmount().fiatAmount();
				this.infoService.setError(this.translationService.translate("buy_amount_larger_than_wallet_balance"));
				return;
			}

			// if we have cash plus balance, use the combined fiat balance to trade, otherwise use just normal fiat balance
			const cashPlusBalance = +this.selectedCashPlusWallet?.assetBalance || 0;
			const totalBalance = cashPlusBalance || this.fiatWallet.getBalance();

			// Buy more than balance
			if (this.data.getType() === TRADE_TYPE.BUY && +this.amount > totalBalance) {
				this.amount = this.maxAmount().fiatAmount();
				this.infoService.setError(this.translationService.translate("buy_amount_larger_than_wallet_balance"));
				return;
			}
			// Amount smaller than min
			if (+this.data.getAmountFiat() < this.min()) {
				this.infoService.setError(this.translationService.translate("transaction_amount_low"));
				this.data.setUserWants(SELECTED_CURRENCY.FIAT);
				this.amount = this.min() + "";
			} else {
				this.data.setAmountDefinition(this.getSelectedCurrency());
				this.callTradeOffer();
			}
		}
	}

	public showTradeSuccessScreen(inputs: {
		offer: Offer;
		itbSuccess: ITBTradeSuccess;
		previousBalance: number;
	}): void {
		combineLatest([
			this.launchDarklyService.isActive$(LD_FLAG.TRADE_SUCCESSFUL_SCREEN_REDESIGN),
			this.tradeOfferService.isFirstTrade$.asObservable(),
			this.tradeOfferService.taxWithheld$.asObservable()
		])
			.pipe(take(1))
			.subscribe(([showRedesignSuccessScreen, isFirstTrade, taxWithheld]) => {
				const combinedInputs = { ...inputs, taxWithheld };
				this.modalService
					.create(showRedesignSuccessScreen ? TradeSuccessV1Component : TradeSuccessComponent, combinedInputs)
					.present();
				const offerType = inputs.offer.getType();
				const capitalizedOfferType = offerType[0].toUpperCase() + offerType.slice(1);
				this.rudderstackService.track({
					name: isFirstTrade ? FIRST_TRADE_EXECUTED : `${capitalizedOfferType} Trade Completed`,
					properties: {
						trade_asset_amount: inputs.offer.getAmountTradable(),
						trade_fiat_amount: inputs.offer.getAmountFiat()
					}
				});
			});
	}

	public fetchTradableWallet(wallet: TradableWallet): void {
		const walletServiceForId = this.walletService.serviceForId(this.data.getWalletId());
		walletServiceForId.upsert(wallet); // Upserting new wallet fetched from API
		const oldWallet: TradableWallet = walletServiceForId.getItemById(this.data.getWalletId());
		this.wallet = wallet; // Important cause the flow references the current wallet via the data.getWalletId()
		walletServiceForId.deleteItem(oldWallet); // Deleting old wallet
	}

	private setCashPlusPortfolioForFiat(): void {
		this.cashPlusEnabled$.pipe(take(1)).subscribe((flag) => {
			if (flag) {
				this.cashPlusPortfolioForFiat$ = this.cashPlusPortfolioFacade.getPortfolioPerFiatState$(
					this.getFiat().getPid()
				);
			}
		});
	}

	private getPayLoadForOffer(): Offer {
		const payLoad: Offer = new Offer();
		payLoad.bestFeeOptIn = this.data.bestFeeOptIn;
		payLoad.setAmountFiat(this.data.getAmountFiat());
		payLoad.setAmountTradable(this.data.getAmountTradable());
		payLoad.setCurrencyId(this.data.getCurrencyId());
		payLoad.setFiatWalletId(this.data.getFiatWalletId());
		payLoad.setFiatId(this.data.getFiatId());
		payLoad.setIndexTrade(this.data.isIndexTrade());
		payLoad.setAmountDefinition(this.data.getAmountDefinition());
		return payLoad;
	}

	private finishOfferCall(res: Offer[]): void {
		this.data = this.tradeOfferService.resetOfferData(res[0], this.data);
		this.showTradeFlow$.next(false);
		this.resetOffer();
		this.finish();
		this.pageChangeSubject.next();
	}

	private getNewOffer(): boolean {
		return this.wallet.isNew();
	}

	public callTradeOffer(): void {
		const shouldGetOfferViaGql = this.shouldCallGql();

		if (this.isIndexTrade()) {
			this.showTradeFlow$.next(false);
			this.finish();
			this.pageChangeSubject.next();
			return;
		}

		if (shouldGetOfferViaGql) {
			this.getOfferFromGql();
			return;
		}

		this.getOfferFromRestApi();
	}

	public get sufficientBestAmount(): boolean {
		const tradeMin: Conversion = this.getPriceValue()?.tradeMin;
		if (this.bestDefaultWallet.getBalance() === 0) {
			return false;
		}
		if (+this.amount === 0) {
			return this.bestDefaultWallet.getBalance() >= +tradeMin.requiredBestAmount;
		}
		if (this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN) {
			return (
				this.bestDefaultWallet.getBalance() >=
				(+this.amount * +tradeMin.requiredBestAmount) / +tradeMin.tradableAmount()
			);
		}

		return (
			this.bestDefaultWallet.getBalance() >=
			(+this.amount * +tradeMin.requiredBestAmount) / +tradeMin.fiatAmount()
		);
	}

	public getFiatWallets(cashPlusWallets?: ICashPlusFiatWallet[]): FiatWallet[] {
		return this.fiatwalletService.list;
	}

	public get amount(): string {
		return this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN
			? this.data.getAmountTradable()
			: this.data.getAmountFiat();
	}

	public set amount(n: string) {
		n = stripFunnyInput(n);
		if (this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN) {
			this.data.setAmountTradable(n);
			this.data.setAmountFiat("" + this.toFiatAmount(+n));
		} else {
			this.data.setAmountFiat(n);
			this.data.setAmountTradable("" + this.toTradableAmount(+n));
		}

		if (this.isIndexTrade() && this.data.getType() === TRADE_TYPE.SELL && n) {
			const maxAmount = this.maxAmount().tradableAmount();
			// We do all the parsing because bp-input rounds fiat amount to 2 decimals using bp-type directive which uses a 3rd party lib called cleave to do the rounding for us. It seems
			// that cleave just strips all the numbers after the second digit, no additional logics.
			if (stringToNumberWithPrecision(n, 2) < stringToNumberWithPrecision(maxAmount, 2)) {
				this.data.setSellAll(false);
			}

			if (stringToNumberWithPrecision(n, 2) === stringToNumberWithPrecision(maxAmount, 2)) {
				this.data.setSellAll(true);
			}
		}
	}

	public toTradableAmount(fiatAmount: number): number {
		return convertFiatToTradable(this.getFiat(), fiatAmount, this.getPriceValue()?.getPrice());
	}

	public toFiatAmount(tradableAmount: number): number {
		return convertTradableToFiat(this.getPriceValue()?.getPrice(), tradableAmount, this.getFiat());
	}

	public getAmountTradable(): number {
		// If more use cases emerge, export to common functions
		const getAmount = (originalAmount: number): number => (!isNaN(originalAmount) ? originalAmount : 0);

		// Selected currency is cryptocoin
		if (this.getSelectedCurrency() === SELECTED_CURRENCY.CRYPTOCOIN) {
			return getAmount(+this.amount);
		}

		// Selected currency is fiat
		let amount = this.amountTradable();
		if (this.data.getType() === TRADE_TYPE.BUY) {
			amount = amount + +this.bfcValue;
		}
		if (this.data.getType() === TRADE_TYPE.SELL) {
			amount = amount - +this.bfcValue;
		}

		return getAmount(amount);
	}

	public getAmountFiat(): number {
		// If more use cases emerge, export to common functions
		const getAmount = (originalAmount: number): number =>
			!isNaN(originalAmount) && originalAmount > 0 ? originalAmount : 0;

		// Selected currency is fiat
		if (this.getSelectedCurrency() === SELECTED_CURRENCY.FIAT) {
			return getAmount(+this.amount);
		}

		// Selected currency is cryptocoin
		let amount = this.amountFiat();
		if (this.data.getType() === TRADE_TYPE.BUY) {
			amount = amount - +this.bfcValue;
		}
		if (this.data.getType() === TRADE_TYPE.SELL) {
			amount = amount + +this.bfcValue;
		}

		return getAmount(amount);
	}

	public amountFiat(): number {
		return +this.data.getAmountFiat();
	}

	public amountTradable(): number {
		return +this.data.getAmountTradable();
	}

	public get isIntroductionStep(): boolean {
		return this.isEdit(-1);
	}

	public get isFirstStep(): boolean {
		return (!this.wallet && this.isEdit(0)) || this.isEdit(1);
	}

	public get isSecondStep(): boolean {
		return (!!this.wallet && !this.fiatWallet && this.isEdit(0)) || this.isEdit(2);
	}

	public get isThirdStep(): boolean {
		return (
			(!!this.getPriceValue() || (!!this.wallet && this.wallet instanceof IndexWallet)) &&
			((!!this.wallet && !!this.fiatWallet && this.isEdit(0)) || this.isEdit(3))
		);
	}

	public get dirty(): boolean {
		return this.edit > 0 || this.summary;
	}

	public fetchLeverageInfo(tradable: Tradable): void {
		if (tradable instanceof LeveragedToken) {
			this.leverageTokenViewModel$ = this.leveragedTokenFacade.fetchLeverageTokenInfo$(tradable.getId()).pipe(
				map((info) => {
					const attributes = info.attributes;

					const currentLeverage = attributes.effective_leverage;
					const basePrice = attributes.last_base_price;
					const overnightFee = info.attributes.overnight_fee;
					const tradableId = this.data.getCurrencyId();
					const fiat = this.getFiat();
					const baseSymbol = attributes.base_asset_symbol;
					const desiredLeverage = attributes.desired_leverage;
					const isLong = +desiredLeverage > 0;
					const documentUrl = attributes?.asset_documents?.[0]?.attributes.url;
					const releverageTime = attributes.releverage_time.date_iso8601;
					const viewModelObject = {
						currentLeverage,
						baseSymbol,
						basePrice,
						overnightFee,
						tradableId,
						fiat,
						desiredLeverage,
						isLong,
						tradable,
						documentUrl,
						releverageTime
					};

					this.leveragedTokenFacade.setLeverageTokenStateInfo$(viewModelObject);
					return viewModelObject;
				})
			);
		}
	}

	// TODO: Remove term check after 03.01.2022
	public selectWallet(wallet: TradableWallet, cashPlusWallets?: ICashPlusFiatWallet[]): void {
		//TODO: If feature flag removed, remove this check
		this.termsAndConditionsService.isDynamictermAndConditionActive$
			.pipe(take(1))
			.subscribe((dynamictermAndConditionActive) => {
				this.preTransBalance = wallet.getBalance();

				if (
					!dynamictermAndConditionActive &&
					wallet.getCurrencyId() === TOKEN_BEST_ID &&
					this.accountService.showBest2TermsOnBestTrade() &&
					this.data.getType() !== TRADE_TYPE.SELL
				) {
					this.handleBest2Terms(wallet);
					return;
				}
				this.trackSelect("wallet");

				if (!this.wallet || this.edit !== 0) {
					this.wallet = wallet;

					if (!!this.amount && +this.amount > 0) {
						// If a new wallet is chosen, amount is set to 0
						this.amount = "0";
					}

					// Is fiat wallet is selected update
					// Else try to select default fiat
					if (this.fiatWallet) {
						this.updateAmounts();
					} else if (this.label === "buy") {
						this.selectSoleFiatWallet(cashPlusWallets);
					} else {
						this.doTrapFocusTime();
					}
					this.edit = 0;

					this.pageChangeSubject.next();
					return;
				}

				this.wallet = wallet;
				this.data.setFiatWalletId("");
				this.data.setFiatId("");
				this.doTrapFocusTime();
				this.edit = 0;
				this.pageChangeSubject.next();
			});
	}

	private selectSoleFiatWallet(cashPlusWallets?: ICashPlusFiatWallet[]): void {
		// Check if we have only one fiat wallet we can select it directly (gitlab issue #809)
		const defaultFiatWallets: FiatWallet[] = this.getFiatWallets(cashPlusWallets);

		if (defaultFiatWallets.length === 1) {
			this.fiatWallet = defaultFiatWallets[0];
			this.updateAmounts();
		} else {
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
		}
	}

	public selectFiatWallet(w: FiatWallet): void {
		this.trackSelect("fiat");
		this.edit = 0;
		this.fiatWallet = w;
		this.updateAmounts();
	}

	public get minLegacy(): number {
		const min =
			this.data.getType() === TRADE_TYPE.BUY || this.data.getType() === "recurring-buy"
				? this.getTradable().getMinBuy()
				: this.getTradable().getMinSell();
		const rate = this.getFiat().toEuroRate();
		return round(min / rate, this.getFiat().getPrecision());
	}

	min = computed(() => {
		const min =
			this.data.getType() === TRADE_TYPE.BUY || this.data.getType() === "recurring-buy"
				? this.getTradable().getMinBuy()
				: this.getTradable().getMinSell();
		const rate = this.getFiat().toEuroRate();
		return this.minFromEstimate()
			? round(this.minFromEstimate(), this.getFiat().getPrecision())
			: round(min / rate, this.getFiat().getPrecision());
	});

	protected updateAmounts(): void {
		this.statsigService
			.isFeatureActive(FEAT_GATES.MARKET_ORDER_DEFAULT_TRADE_API_SWITCH)
			.pipe(take(1))
			.subscribe((estimatePriceEnabled) => {
				const tradable = this.tradableListService.getItemById(this.data.getCurrencyId());
				const isIndex = tradable.getCurrencyType() === TRADABLES_TYPE.INDEX;
				if (estimatePriceEnabled) {
					if (isIndex) {
						const payload: EstimateIndexPricesQueryVariables = {
							input: {
								type: TradeType.Sell,
								indexId: tradable.getPid(),
								fiatId: this.fiatService.getItemById(this.fiatWallet.getCurrencyId()).getPid()
							}
						};
						this.checkoutRemote.estimateIndexPrices(payload).subscribe({
							next: (price) => {
								this.handlePriceSuccess(mapEstimateIndexPricesToPrice(price));
							},
							error: (err) => {
								this.infoService.setError(err);
							}
						});
					} else {
						const isGeneratedWallet = this.data.getWalletId().includes("generated");
						const estimatePricesPayload: EstimatePricesQueryVariables = {
							input: {
								assetId: tradable.getPid(),
								fiatId: this.fiatService.getItemById(this.fiatWallet.getCurrencyId()).getPid(),
								assetWalletId: isGeneratedWallet ? null : this.wallet.getId(),
								type: this.data.getType() === TRADE_TYPE.BUY ? TradeType.Buy : TradeType.Sell
							}
						};
						this.checkoutRemote.estimatePrices(estimatePricesPayload).subscribe({
							next: (price) => {
								if (price && price.appropriatenessTest.required) {
									this.apprServiceHelper.startTest(null, true);
								}
								this.handlePriceSuccess(mapEstimatePricesToPrice(price));
							},
							error: (err) => {
								this.infoService.setError(err);
							}
						});
					}
				} else {
					const a = new ApicallOption(Price, this.getNewOffer() ? this.getPayLoadForOffer() : this.data);
					this.apicallService.post("trade/prices/default", a).subscribe(
						(price) => {
							this.handlePriceSuccess(price[0]);
						},

						(error) => {
							if (error instanceof OfflineError) {
								this.modalService.create(OfflineModalComponent).present();
							}

							if (error instanceof ErrorClass) {
								this.infoService.setError(error);

								// trigger appropriateness test if code return match appr_test is required
								if (error.containsCode(APPROPRIATENESS_REQUIRED_ERROR_CODE)) {
									this.apprServiceHelper.startTest(null, true);
								}
							} else {
								this.infoService.setError("error");
							}
							this.priceSubject$.next(null);
							this.tradableId = null;
							this.renewWallet();
						}
					);
				}
			});
	}

	private handlePriceSuccess(price: Price): void {
		this.priceSubject$.next(price);
		this.minFromEstimate.set(+price.tradeMin.getFiat());

		if (!this.data.isDirectBestOffer()) {
			this.data.setUserWants(SELECTED_CURRENCY.FIAT);
			// eslint-disable-next-line no-self-assign
			this.amount = this.amount;
		} else {
			// eslint-disable-next-line no-self-assign
			this.amount = this.amount; // This is NOT a mistake -> the two halves of this if-statement are related to completely different flow, and the logic inside the amount setters does different things based on prior fields set in the flows.
			this.getOffer();
		}
		this.doTrapFocusTime();
		this.pageChangeSubject.next();

		this.fetchLeverageInfo(this.tradableListService.getItemById(this.wallet.getCurrencyId()));
	}

	// TODO: Remove after 03.01.2022
	public handleBest2Terms(wallet: TradableWallet): void {
		this.modalService.getCurrentModal().hide();
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.selectWallet(wallet);
			} else {
				this.resetTradable();
			}
		});
	}

	public maxAmount(): Conversion {
		const price = this.getPriceValue() ? this.getPriceValue() : new Price();
		return price.getMaxAmount();
	}

	public getPrice(): Conversion {
		return this.getPriceValue()?.getPrice();
	}

	public getPriceFiat(): string {
		return this.data.bestFeeOptIn
			? this.getPriceValue()?.getPrice().fiatBfc
			: this.getPriceValue()?.getPrice().getFiat();
	}

	public getAmounts(): Conversion[] {
		if (this.getPriceValue() == null) {
			return [];
		}

		return this.getPriceValue()?.getAmounts().slice(0, 2);
	}

	public getAppTestTradingWarning(): boolean {
		return this.getPriceValue()?.getAppTestTradingWarning();
	}

	public setAmount(n: Conversion): void {
		this.data.setAmountFiat(this.data.bestFeeOptIn ? n.fiatBfc : n.fiatAmount());
		this.data.setAmountTradable(this.data.bestFeeOptIn ? n.cryptoBfc : n.tradableAmount());
		this.data.setUserWants(SELECTED_CURRENCY.FIAT);
		this.trackSelect("amount");

		const fiat = this.fiatService.getItemById(this.data.getFiatId());
		const euroAmount = toEuroByFiatId(+n.fiatAmount(), fiat);

		if (this.data.getType() === TRADE_TYPE.SELL) {
			if (this.isIndexTrade()) {
				if (euroAmount === +this.getPriceValue()?.getMaxAmount().fiatAmount()) {
					this.data.setSellAll(true);
				} else {
					this.data.setSellAll(false);
				}
			}
		}

		this.rudderstackTrack("Specific Amount Button Used", {
			button_amount: n.fiatAmount(),
			currency: this.getFiat().getSymbol().toLowerCase(),
			flow: this.flowName
		});
	}

	public setToMax(): void {
		if (this.data.getType() === TRADE_TYPE.BUY) {
			this.data.setUserWants(SELECTED_CURRENCY.FIAT);
			this.amount = this.maxAmount().fiatAmount();
		} else {
			if (this.isIndexTrade()) {
				this.data.setSellAll(true);
				this.data.setUserWants(SELECTED_CURRENCY.FIAT);
			} else {
				this.data.setUserWants(SELECTED_CURRENCY.CRYPTOCOIN);
			}

			this.amount = this.data.bestFeeOptIn ? this.maxAmount().cryptoBfc : this.maxAmount().tradableAmount();
		}
		this.trackSelect("amount");

		this.rudderstackTrack("Max Amount Button Used", {
			button_amount: "" + this.amount,
			currency: this.getFiat().getSymbol().toLowerCase(),
			flow: this.flowName
		});
	}

	public stepBack(): void {
		if (this.data) {
			this.data.stepBack();
		}
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public resetTradable(): void {
		this.tradableId = null;
		this.doTrapFocusTime();
	}

	public inputOnFocus(e: FocusEvent): void {
		this.amountChanged = false;
	}

	public inputOnFocusOut(e: FocusEvent): void {
		if (this.amountChanged) {
			this.trackSelect("amount_input");
		}
	}

	public inputModelChange(e: Event): void {
		this.amountChanged = true;
	}

	private resetOffer(): void {
		if (this.offer != null) {
			this.offer.setOfferTimer();
		}
	}

	protected renewWallet(): void {
		if (this.getTradable() instanceof Cryptocoin) {
			this.wallet = new CryptoWallet();
		} else {
			this.wallet = new MetalWallet();
		}
	}

	public initTracking(): void {
		this.pageChangeSubject.pipe(untilDestroyed(this)).subscribe(() => {
			if (this.summary) {
				this.rudderstackPageFlow("Summary", "summary", {
					asset_category: this.getTradable().getSymbol(true),
					fiat_currency: this.getFiat().getSymbol(true),
					amount: this.amount,
					bfc_enabled: this.data.bestFeeOptIn
				});
			} else if (this.isThirdStep) {
				this.rudderstackPageFlow("Amount Selection", "amount", {
					asset_category: this.getTradable().getSymbol(true),
					fiat_currency: this.getFiat().getSymbol(true)
				});
			} else if (this.isSecondStep) {
				this.rudderstackPageFlow("Fiat Selection", "fiatwallet", {
					asset_category: this.getTradable().getSymbol(true)
				});
			} else if (this.isFirstStep) {
				this.rudderstackPageFlow("Asset Selection");
			}
		});
	}

	public bfcToggleClicked(value: boolean): void {
		this.rudderstackTrack("Bfc Toggle Used", {
			bfc_status: value ? "enabled" : "disabled",
			flow: this.flowName
		});
	}

	public shouldCallGql(): boolean {
		return this.isTradingCoin() || this.isTradingMetal() || this.canTradeDerivative() || this.canTradeBci();
	}

	private isTradingCoin(): boolean {
		return this.getTradable() instanceof Cryptocoin;
	}

	private isTradingMetal(): boolean {
		return this.getTradable() instanceof Metal;
	}

	// Stocks and ETFs
	private canTradeDerivative(): boolean {
		return this.canTradeDerivativesViaGql && this.isTradingDerivative();
	}

	private isTradingDerivative(): boolean {
		return this.getTradable() instanceof Derivative;
	}

	private canTradeBci(): boolean {
		return this.canTradeBciViaGql && this.isIndexTrade();
	}

	private getOfferFromRestApi(): void {
		this.tradeOfferService.callTradeOffer(this.getNewOffer() ? this.getPayLoadForOffer() : this.data).subscribe(
			(ret) => {
				if (this.getNewOffer()) {
					this.apicallService
						.get(
							"wallet/" + ret[0].getWalletId(),
							new ApicallOption(this.walletService.serviceForId(this.data.getWalletId()).WalletType)
						) // Fetching the new wallet is only necessary in case no wallet existed in the first place
						.subscribe(
							(a: TradableWallet[]) => {
								this.fetchTradableWallet(a[0]); // Only necessary in case no prior wallet exists
								this.finishOfferCall(ret); // Has to happen AFTER new wallet is set
							},
							(error1) => Helper.handleError(error1)
						);
				} else {
					this.finishOfferCall(ret);
				}
			},
			(error) => {
				this.handleErrorOnOfferCreate(error);
			}
		);
	}

	private getOfferFromGql(): void {
		const offer = this.getNewOffer() ? this.getPayLoadForOffer() : this.data;
		// If the offer is new (new wallet) we don't set the wallet_id (this.getPayLoadForOffer() removes the wallet_id). But, we
		// Need the wallet_id later on before we accept the offer (tradabla-trade-summary.component on the acceptOfferGql() method
		// Where we check again whether the wallet is new or not).
		offer.setWalletId(this.data.getWalletId());

		this.tradeOfferService.createOfferGQL(offer).subscribe(
			(o) => {
				this.finishOfferCall([o]);
			},
			(error) => {
				this.handleErrorOnOfferCreate(error);
			}
		);
	}

	private onGqlFeatureFlagChange(): void {
		combineLatest([
			this.launchDarklyService.isFeatureActive$(LD_FLAG.GQL_TRADE_DERIVATIVES),
			this.launchDarklyService.isFeatureActive$(LD_FLAG.GQL_TRADE_BCI)
		]).subscribe(([canTradeDerivativesViaGql, canTradeBciViaGql]) => {
			this.canTradeDerivativesViaGql = canTradeDerivativesViaGql;
			this.canTradeBciViaGql = canTradeBciViaGql;
		});
	}

	private handleErrorOnOfferCreate(error: ErrorClass | Error): void {
		if (error instanceof ErrorClass) {
			this.resetOffer();
			this.infoService.setError(error);

			if (error instanceof ValidationError) {
				this.data.setValidationErrors(error);
				const meta = error.responseErrors[0];
				if (meta.hasOwnProperty("meta")) {
					const amount = meta.meta.amount;
					if (amount !== undefined) {
						this.data.setAmountFiat(amount);
						this.data.setAmountTradable(
							"" + convertFiatToTradable(this.getFiat(), amount, this.getTradable())
						);
						this.data.setUserWants(SELECTED_CURRENCY.FIAT);
					}
				}
			} else if (error instanceof LimitError) {
				const meta = error.responseErrors[0];
				if (!meta.hasOwnProperty("meta")) {
					return;
				}
				const amount = meta.meta.amount;
				if (amount === undefined) {
					return;
				}
				if (meta.meta.hasOwnProperty("cryptocoin_id")) {
					this.data.setAmountTradable(amount);
					this.data.setAmountFiat("" + convertTradableToFiat(this.getTradable(), amount, this.getFiat()));
					this.data.setUserWants(SELECTED_CURRENCY.CRYPTOCOIN);
				} else {
					this.data.setAmountFiat(amount);
					this.data.setAmountTradable("" + convertFiatToTradable(this.getFiat(), amount, this.getTradable()));
					this.data.setUserWants(SELECTED_CURRENCY.FIAT);
				}
			}
		} else {
			this.resetOffer();
			console.error(error);
		}
	}

	public getItbData(): ITBTrade {
		let amount = "";
		if (+this.data?.getAmountFiat() >= this.min()) {
			amount = "" + +this.data?.getAmountFiat() * this.getFiat()?.toEuroRate();
		}
		if (+this.data?.getAmountFiat() > +this.maxAmount()?.fiatAmount()) {
			amount = "" + +this.maxAmount().fiatAmount() * this.getFiat()?.toEuroRate();
		}

		return {
			amount: amount,
			itbRate: this.getPriceValue()?.getItb()
		};
	}

	public getItbStep(): string {
		if (this.isThirdStep) {
			return ITBStep.AMOUNT;
		}
		return "";
	}

	protected hasItb(): boolean {
		return !!this.getPriceValue()?.getItb() && +this.getPriceValue()?.getItb() !== 0;
	}

	public updateTradeId(id: string): void {
		this.successTradeId = id;
	}

	public getItbTradeSuccess(): ITBTradeSuccess {
		return {
			hasItb: this.hasItb(),
			tradeId: this.successTradeId,
			useGQL: this.shouldCallGql()
		};
	}
}
